import React, { useEffect, useState } from 'react';
import { TFunction } from 'i18next';
import { DatePicker, Table, Tooltip } from 'antd';

import constants from '../../../constants';
import { CircleFill } from 'react-bootstrap-icons';
import { ColumnsType } from 'antd/lib/table';
import { DateTime } from 'luxon';
import { ExpandableConfig } from 'antd/lib/table/interface';
import { OrderProductTypeRefundableInfo, OrderRefundableInfo } from '../../../Model/orderRefundable';
import moment from 'moment';
import { Col, Row } from 'react-bootstrap';
import { WarningFilled } from '@ant-design/icons';
import Loader from '../../../common-components/loader';
import authAxios from '../../../authorization/axios';

interface RefundableCheckProps {
  t: TFunction,
  orderId: number
}

const FriendlyNameColumn = ({ value, record, t }: { value: string, record: OrderProductTypeRefundableInfo, t: TFunction }) => <>
  <div>{value}</div>
  {
    record.isReundableFromAnotherOrder &&
    <div style={{ display: 'flex', alignItems: 'center', marginTop: '1em' }}>
      <WarningFilled style={{ marginRight: 10, color: '#FFCC00' }}
        size={15}
        color={'yellow'}></WarningFilled>
      <small>{t('REFUNDABLE_FROM_ANOTHER_ORDER')}</small>
    </div>
  }
</>;

const StatusIcon = ({ value, getRefundableStatus }: { value: string, getRefundableStatus: Function }) =>
  <Tooltip title={getRefundableStatus(value, 'label')}>
    <CircleFill style={{ marginRight: 20, marginBottom: 5 }}
      size={10}
      color={getRefundableStatus(value, 'color')}></CircleFill>
  </Tooltip>;

const RefundableCheck = ({ t, orderId }: RefundableCheckProps) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<OrderRefundableInfo>();
  const [date, setDate] = useState<Date>(new Date());

  const getOrderRefoundableInfo = (orderID: number, dateToCheck?: Date) => {
    authAxios.get(constants.api.orderRefudable.replace('{id}', `${orderID}`), {
      params: {
        date: dateToCheck
      }
    }).then((res: any) => {
      if (res.data?.success) {
        const items = res.data.data.items.map((d: any, index: number) => ({ ...d, key: index }));
        res.data.data.items = items;
        setData(res.data.data);
        setLoading(false);
      }
    });
  };

  const getRefundableStatus = (status: 'full' | 'partial' | 'non-refundable', type: 'color' | 'label') => {
    switch (status) {
      case 'full':
        return type == 'color' ? 'green' : t('REFUNDABLE');
      case 'partial':
        return type == 'color' ? '#FFCC00' : t('PARTIAL_REFUNDABLE');
      case 'non-refundable':
        return type == 'color' ? 'red' : t('NO_REFUNDABLE');
      default:
        break;
    }
  };

  useEffect(() => {
    getOrderRefoundableInfo(orderId, date);
  }, []);

  const expandable: ExpandableConfig<OrderProductTypeRefundableInfo> = {
    rowExpandable: (record) => (record.items?.length || 0) > 0,
    expandedRowRender: (record) => {
      return <table>
        <thead>
          <tr>
            <th></th>
            <th>{t('NFT')}</th>
            <th>{t('PRODUCT_NAME')}</th>
            <th>{t('REFUNDABLE_DATE')}</th>
          </tr>
        </thead>
        <tbody>
          {
            record.items.map((i, index) => <tr key={`exp-${index}`}>
              <td>
                <Tooltip title={getRefundableStatus(i.refundable, 'label')}>
                  <CircleFill style={{ marginRight: 20, marginBottom: 5 }}
                    size={10}
                    color={getRefundableStatus(i.refundable, 'color')}></CircleFill>
                </Tooltip>
              </td>
              <td>{i.licenseNft || '-'}</td>
              <td>{record.friendlyName}</td>
              <td>{i.refundableDate ? DateTime.fromJSDate(new Date(i.refundableDate)).toFormat('dd/MM/yyyy HH:mm') : '-'}</td>
            </tr>)
          }
        </tbody>
      </table> as any;
    }
  };

  const refundableColumns: ColumnsType<OrderProductTypeRefundableInfo> = [
    {
      dataIndex: 'refundable',
      key: 'refundable',
      render: (value: 'full' | 'partial' | 'non-refundable') => StatusIcon({ getRefundableStatus, value })
    },
    {
      title: t('PRODUCT_NAME'),
      dataIndex: 'friendlyName',
      key: 'friendlyName',
      render: (value: string, record: OrderProductTypeRefundableInfo) => FriendlyNameColumn({ t, record, value })
    },
    {
      title: t('REFUNDABLE_DATE'),
      dataIndex: 'refundableDate',
      key: 'refundableDate',
      render: (value: Date) => DateTime.fromJSDate(new Date(value)).toFormat('dd/MM/yyyy HH:mm')
    },
    {
      title: t('REFUNDABLE_ITEM'),
      dataIndex: 'refundableitems',
      key: 'refundableitems',
      render: (value: number, record: OrderProductTypeRefundableInfo) => `${value} / ${record.totalItems}`
    },
    {
      title: t('ACTIVATED_LICENSES'),
      dataIndex: 'activatedLicenses',
      key: 'activatedLicenses',
      render: (value: number, record: OrderProductTypeRefundableInfo) => {
        return `${record.items.filter(i => i.licenseActivationDate).length} / ${record.totalItems}`;
      }
    }
  ];

  return <>
    <Loader visible={loading} />
    {
      !loading && data && <>
        <Row style={{ marginBottom: '1em' }}>
          <Col>
            <h2>{data.orderId}</h2>
          </Col>
        </Row>
        <Row style={{ marginBottom: '1em' }}>
          <Col>
            <Tooltip title={getRefundableStatus(data.refundable, 'label')}>
              <CircleFill style={{ marginRight: 20, marginBottom: 5 }}
                size={15}
                color={getRefundableStatus(data.refundable, 'color')}></CircleFill>
            </Tooltip>
            <span>
              {getRefundableStatus(data.refundable, 'label')}
            </span>
            {
              data.isContactBanned &&
              <div style={{ display: 'flex', alignItems: 'center', marginTop: '1em' }}>
                <WarningFilled style={{ marginRight: 10, color: '#FFCC00' }}
                  size={15}
                  color={'yellow'}></WarningFilled>
                <small>{t('CONTACT_BANNED')}</small>
              </div>
            }
          </Col>
          <Col style={{ textAlign: 'right' }}>
            <DatePicker
              value={moment(date)}
              allowClear={false}
              format={t('DATE_FORMAT')}
              onChange={(selectedDate) => {
                if (selectedDate) {
                  setDate(selectedDate.toDate());
                  getOrderRefoundableInfo(orderId, selectedDate.toDate());
                }
              }} />
          </Col>
        </Row>
        <Table
          columns={refundableColumns}
          dataSource={data.items}
          expandable={expandable}
        />
      </>
    }
  </>;
};


export default RefundableCheck;